import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PoweredByOutgrow } from "@/components/shared";
import { useBranding } from "@/context/BrandingContext";
import { ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&q=85";

function AuthShell({ children }) {
  const { logo } = useBranding();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block bg-[#303E49] overflow-hidden">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" aria-hidden="true" />
        <div className="absolute inset-0 bp-grid opacity-40" aria-hidden="true" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="EnterpriseSG" className="h-10 max-w-[220px] object-contain bg-white/95 rounded px-2 py-1" data-testid="brand-logo-auth" />
            ) : (
              <>
                <ShieldCheck className="h-8 w-8 text-brand" strokeWidth={1.75} aria-hidden="true" />
                <div>
                  <div className="font-heading font-extrabold text-lg tracking-tight">EnterpriseSG</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Assessment Platform</div>
                </div>
              </>
            )}
          </div>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight max-w-md">
              Partner Assessment &amp; Independent Validation.
            </h2>
            <p className="mt-4 text-slate-300 text-sm max-w-md leading-relaxed">
              Versioned submissions, role-based scoring, governance follow-ups and a fully
              auditable trail — built for trust and immutability.
            </p>
            <div className="mt-8 flex gap-6 font-mono text-xs text-slate-400">
              <span>· RBAC ENFORCED</span>
              <span>· IMMUTABLE VERSIONS</span>
              <span>· WCAG 2.1 AA</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          {children}
          <div className="mt-10 flex justify-center">
            <PoweredByOutgrow />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function GoogleButton() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleGoogle}
      data-testid="google-login-button"
      className="w-full rounded-md"
    >
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
        <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
        <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z" />
      </svg>
      Continue with Google
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="overline mb-2">Secure sign in</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Welcome back</h1>
      <p className="text-sm text-muted-foreground mb-6">Sign in to your EnterpriseSG account.</p>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="login-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input" className="mt-1.5 rounded-md" placeholder="you@organisation.sg" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input" className="mt-1.5 rounded-md" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full rounded-md">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
          Sign in
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton />

      <p className="mt-6 text-sm text-muted-foreground text-center">
        New partner (TAC)?{" "}
        <Link to="/register" className="font-medium text-foreground underline" data-testid="register-link">
          Request an account
        </Link>
      </p>
    </AuthShell>
  );
}

export { AuthShell };
